import {
    Box,
    Divider,
    IconButton,
    Link,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Paper,
    Popover,
    styled,
    Typography
} from '@mui/material';
import React from 'react';
import { AutomationCondition, ConditionLink } from '../rules.types';
import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';
import { addOpacity } from 'lib/helpers';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { RelatedRulesLink } from './RelatedRulesLink';

interface ConditionVisualisationData {
    error?: string | string[];
    showErrors?: boolean;
    label: string;
    condition: AutomationCondition;
    links?: ConditionLink[];
}

interface AutomationNodeConditionPopupProps {
    anchor: Element;
    onClose?: () => void;
    data: ConditionVisualisationData;
}

const StyledCodeBackground = styled(Box)(({ theme }) => ({
    width: '100%',
    backgroundColor: addOpacity(theme.palette.text.primary, 0.05),
    padding: theme.spacing(1)
}));

const StyledTextButton = styled(Typography)(({ theme }) => ({
    cursor: 'pointer',
    textDecoration: 'underline',
    color: theme.palette.text.primary,
    fontSize: '0.75rem',
    padding: theme.spacing(0, 1, 1, 1)
}));

const MainText = styled(Typography)(({ theme }) => ({
    fontSize: '0.9rem',
    color: theme.palette.text.primary
}));

const SecondaryText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    opacity: 0.75,
    fontSize: '0.7rem'
}));

export const AutomationNodeConditionPopup: React.FC<AutomationNodeConditionPopupProps> = ({
    anchor,
    onClose,
    data
}) => {
    const [showCode, setShowCode] = React.useState(false);
    const handleShowCode = React.useCallback(() => {
        setShowCode(true);
    }, []);
    const handleHideCode = React.useCallback(() => {
        setShowCode(false);
    }, []);
    const errors = React.useMemo(() => (Array.isArray(data.error) ? data.error : [data.error]), [data.error]);
    const getLink = React.useCallback((link: string) => `${window.location.origin}${link}`, []);

    return (
        <Popover
            open={!!anchor}
            anchorEl={anchor}
            aria-hidden="false"
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
            }}
            disableRestoreFocus
            onClose={onClose}
        >
            <Paper elevation={3} sx={{ minWidth: 200, maxWidth: 500 }}>
                <Box p={0.5}>
                    <Typography variant="body2" align="center">
                        Condition
                    </Typography>
                </Box>
                <Divider />
                <Box p={1}>
                    <MainText sx={{ fontSize: '1rem' }} variant="body1">
                        {data.label}
                    </MainText>
                </Box>
                {data.showErrors && errors && (
                    <Box p={1}>
                        <SecondaryText variant="body2">Errors</SecondaryText>
                        {errors.map(error => (
                            <MainText
                                variant="body1"
                                key={error}
                                sx={{
                                    opacity: 0.8,
                                    color: 'error.main'
                                }}
                            >
                                {error}
                            </MainText>
                        ))}
                    </Box>
                )}
                {data.links && data.links.length > 0 && (
                    <Box p={1}>
                        <SecondaryText variant="body2" sx={{ opacity: 0.75, fontSize: '0.7rem' }}>
                            Links
                        </SecondaryText>
                        <List dense disablePadding>
                            {data.links.map((link, i) => (
                                <React.Fragment key={link.link}>
                                    {i > 0 && <Divider variant="inset" />}
                                    <ListItem disableGutters>
                                        <ListItemText
                                            sx={{ fontSize: '0.8rem' }}
                                            primary={link.label}
                                            secondary={
                                                link.rulesLink ? (
                                                    <RelatedRulesLink to={link.rulesLink}>
                                                        Show related automations
                                                    </RelatedRulesLink>
                                                ) : undefined
                                            }
                                        />
                                        {link.link && (
                                            <ListItemSecondaryAction>
                                                <Link
                                                    href={getLink(link.link)}
                                                    rel="noopener noreferrer"
                                                    target="_blank"
                                                >
                                                    <IconButton>
                                                        <OpenInNewIcon />
                                                    </IconButton>
                                                </Link>
                                            </ListItemSecondaryAction>
                                        )}
                                    </ListItem>
                                </React.Fragment>
                            ))}
                        </List>
                    </Box>
                )}
                {data.condition && (
                    <Box>
                        {!showCode && (
                            <StyledTextButton align="right" onClick={handleShowCode}>
                                Show condition code
                            </StyledTextButton>
                        )}
                        {showCode && (
                            <React.Fragment>
                                <StyledTextButton align="right" onClick={handleHideCode}>
                                    Hide condition code
                                </StyledTextButton>
                                <StyledCodeBackground>
                                    <JsonView src={data.condition} collapsed={2} />
                                </StyledCodeBackground>
                            </React.Fragment>
                        )}
                    </Box>
                )}
            </Paper>
        </Popover>
    );
};
